import { dv } from '@aviv-estimation-funnel/data-validation/dv';
import type { FieldValidator } from '@aviv-estimation-funnel/data-validation/field-validator';
import {
    EpcEnergyEfficiencyRating,
    EstateType,
    GermanEstateSubTypeApartment,
    GermanEstateSubTypeHouse,
    GermanRegionCodes,
    Job,
    JobRole,
    NonOwnerObjective,
    OwnerObjective,
    PropertyCondition,
    PropertyMainUsage,
    RegionCode,
    Salutation,
    ViewFromProperty,
} from '@aviv-estimation-funnel/estimation-data-models/enums';
import { validatePhone } from '@mfe/app/services/phoneValidationApiRequests';
import { submitForm } from '@mfe/app/services/submitFormApiRequests';
import {
    formatFloatInputValue,
    formatFloatRange,
    formatStringWithPlusSign,
} from '@mfe-helpers/format';
import submitFormattedForm from '@mfe-helpers/formHelpers';
import { getPhoneNumberRegionCodeValues } from '@mfe-helpers/phoneNumberRegionCode';
import {
    defaultAutocompleteField,
    defaultCheckboxField,
    defaultCustomIncrementalField,
    defaultIncrementalField,
    defaultMaxRangeField,
    defaultNumberField,
    defaultRadioBooleanField,
    defaultRadioField,
    defaultSelectField,
    defaultTextField,
} from '@mfe-presentation/constants/defaultFields';
import useSmsService from '@mfe-presentation/hooks/useSmsService';
import {
    EConstructionPeriod,
    EMediaComponent,
    EStepComponent,
    EUIComponent,
    ParentBenefit,
} from '@mfe-types/enum';
import {
    AutoCompleteField,
    CheckboxField,
    IncrementalField,
    MaxRangeField,
    NumberField,
    RadioBooleanField,
    RadioField,
    SelectField,
    TextField,
} from '@mfe-types/field';
import { IStep } from '@mfe-types/form';
import { TLocale } from '@aviv-estimation-funnel/shells/src/app/helpers/i18n';

type ApartmentAdditionalBenefits = {
    hasAnElevator: CheckboxField;
    hasAPrivateGarden: CheckboxField;
    privateGardensSurface: MaxRangeField;
    hasABalconyOrTerrace: CheckboxField;
    balconiesAndTerracesSurface: MaxRangeField;
    hasACellar: CheckboxField;
    hasAnOutdoorParking: CheckboxField;
    numberOfOutdoorParkingLots: IncrementalField;
    hasAnIndoorParking: CheckboxField;
    numberOfIndoorParkingLots: IncrementalField;
    viewFromProperty: RadioField;
};
type HouseAdditionalBenefits = {
    hasABasement: CheckboxField;
    basementsSurface: MaxRangeField;
    hasABalconyOrTerrace: CheckboxField;
    balconiesAndTerracesSurface: MaxRangeField;
    hasAnOutdoorParking: CheckboxField;
    numberOfOutdoorParkingLots: IncrementalField;
    hasAnIndoorParking: CheckboxField;
    numberOfIndoorParkingLots: IncrementalField;
    hasASwimmingPool: CheckboxField;
    viewFromProperty: RadioField;
};

const commonNumberOfRoomsField = (validator: FieldValidator<unknown>): IncrementalField => ({
    ...defaultIncrementalField,
    formatInputValue: formatFloatRange,
    inputMode: 'decimal',
    validators: validator,
    increment: 0.5,
    maxValue: 100,
    minValue: 0.5,
});

export interface ICommonStepsConfiguration extends IStep {
    address: {
        displayedStepNumber: number;
        stepComponent: EStepComponent;
        getFields: () => {
            postalCode: TextField;
            cityName: SelectField;
            streetName: AutoCompleteField;
            houseNumber: TextField;
        };
    };
    estateType: {
        displayedStepNumber: number;
        getFields: () => {
            estateType: RadioField;
        };
    };
    characteristics: {
        displayedStepNumber: number;
        getFields: () => {
            estateSubType: SelectField;
            livingSurface: NumberField;
            totalSiteSurface: NumberField;
            numberOfRooms: IncrementalField;
            numberOfBathRooms: IncrementalField;
            floorNumber: IncrementalField;
            numberOfFloorsInBuilding: IncrementalField;
            monthlyCondominiumFee: NumberField;
        };
    };
    benefits: {
        displayedStepNumber: number;
        getFields: (
            fieldValues: Record<string, any>,
        ) => HouseAdditionalBenefits | ApartmentAdditionalBenefits;
    };
    conditions: {
        displayedStepNumber: number;
        getFields: () => {
            constructionYear: MaxRangeField;
            constructionPeriod: SelectField;
            epcEnergyEfficiencyRating: SelectField;
            averageCondition: RadioField;
        };
    };
    profile: {
        displayedStepNumber: number;
        stepComponent: EStepComponent;
        getFields: () => {
            isAPrivatePerson: RadioBooleanField;
            job: SelectField;
            jobRole: SelectField;
            isPropertyOwner: RadioBooleanField;
            propertyMainUsage: SelectField;
            ownerObjective: SelectField;
            nonOwnerObjective: SelectField;
        };
    };
    identity: {
        displayedStepNumber: number;
        termsAndConditions?: {
            termsAndConditionsOfServiceUrl: Record<string, string>;
            privacyPolicyUrl: Record<string, string>;
            avivWebsiteUrl: Record<string, string>;
        };
        stepComponent: EStepComponent;
        getFields: () => {
            salutation: RadioField;
            firstName: TextField;
            lastName: TextField;
            phoneNumberRegionCode: SelectField;
            phoneNumber: TextField;
            email: TextField;
        };
        httpCalls: Record<string, (...args: any) => Promise<any>>;
        customSubmit: (
            fieldValues: Record<string, any>,
            config: IStep,
            name: string,
            locale: TLocale,
        ) => Promise<any>;
    };
    verification: {
        displayedStepNumber: number;
        stepComponent: EStepComponent;
        getFields: () => {
            phoneNumberVerificationCode: TextField;
        };
        httpCalls: Record<string, (...args: any) => Promise<any>>;
        customSubmit: (
            fieldValues: Record<string, any>,
            config: IStep,
            name: string,
            locale: TLocale,
        ) => Promise<any>;
    };
    loading: {
        displayedStepNumber: number;
        getFields: () => {};
    };
}

export const commonEstateSubTypeField = (validator: FieldValidator<unknown>): SelectField => ({
    ...defaultSelectField,
    validators: validator,
    options: {
        values: (fieldValues: Record<string, any>) => {
            if (fieldValues.estateType === EstateType.Apartment) {
                return [
                    { value: GermanEstateSubTypeApartment.Apartment },
                    { value: GermanEstateSubTypeApartment.DuplexOrTriplex },
                    { value: GermanEstateSubTypeApartment.Penthouse },
                ];
            }

            return [
                { value: GermanEstateSubTypeHouse.SingleFamilyHouse },
                { value: GermanEstateSubTypeHouse.MultiFamilyHouse },
                { value: GermanEstateSubTypeHouse.SemiDetachedHouse },
                { value: GermanEstateSubTypeHouse.TerraceHouse },
                { value: GermanEstateSubTypeHouse.EndTerraceHouse },
            ];
        },
    },
});

export const commonLivingSurfaceField = (validator: FieldValidator<unknown>): NumberField => ({
    ...defaultNumberField,
    inputMode: 'decimal',
    formatInputValue: formatFloatInputValue,
    validators: validator,
});

export const commonTotalSiteSurfaceField = (validator: FieldValidator<unknown>): NumberField => ({
    ...defaultNumberField,
    formatInputValue: formatFloatInputValue,
    inputMode: 'decimal',
    validators: validator,
    displayedConditions: [
        (fieldValues: Record<string, any>) => fieldValues.estateType === EstateType.House,
    ],
    isRequired: false,
});

export const commonNumberOfBathRoomsField = (
    validator: FieldValidator<unknown>,
): IncrementalField => ({
    ...defaultIncrementalField,
    validators: validator,
    maxValue: 100,
});

export const getPropertyFields =
    (validators: Record<string, FieldValidator<unknown>>) =>
    (): {
        isPropertyOwner: RadioBooleanField;
        propertyMainUsage: SelectField;
        ownerObjective: SelectField;
        nonOwnerObjective: SelectField;
    } => ({
        isPropertyOwner: {
            ...defaultRadioBooleanField,
            uiComponent: EUIComponent.Radio,
            validators: validators.isPropertyOwner,
            displayedConditions: [
                (fieldValues: Record<string, any>) => fieldValues.isAPrivatePerson,
            ],
            options: {
                values: [
                    { value: true, translationKey: 'yes' },
                    { value: false, translationKey: 'no' },
                ],
            },
        },
        propertyMainUsage: {
            ...defaultSelectField,
            validators: validators.propertyMainUsage,
            displayedConditions: [
                (fieldValues: Record<string, any>) =>
                    fieldValues.isAPrivatePerson && fieldValues.isPropertyOwner,
            ],
            options: {
                values: [
                    { value: PropertyMainUsage.PrincipalResidence },
                    { value: PropertyMainUsage.SecondaryResidence },
                    { value: PropertyMainUsage.RentalResidence },
                    { value: PropertyMainUsage.Other },
                ],
            },
        },
        ownerObjective: {
            ...defaultSelectField,
            validators: validators.ownerObjective,
            displayedConditions: [
                (fieldValues: Record<string, any>) =>
                    fieldValues.isAPrivatePerson &&
                    fieldValues.isPropertyOwner &&
                    fieldValues.propertyMainUsage,
            ],
            options: {
                values: [
                    { value: OwnerObjective.StartedSelling },
                    { value: OwnerObjective.WantsToSellAsap },
                    { value: OwnerObjective.WantsToSellInLessThanThreeMonths },
                    { value: OwnerObjective.WantsToSellInLessThanSixMonths },
                    { value: OwnerObjective.WantsToSellInLessThanOneYear },
                    { value: OwnerObjective.WantsToSellInMoreThanOneYear },
                    { value: OwnerObjective.NoSaleProject },
                ],
            },
        },
        nonOwnerObjective: {
            ...defaultSelectField,
            validators: validators.nonOwnerObjective,
            displayedConditions: [
                (fieldValues: Record<string, any>) =>
                    fieldValues.isAPrivatePerson && !fieldValues.isPropertyOwner,
            ],
            options: {
                values: [
                    { value: NonOwnerObjective.WantsToBuy },
                    { value: NonOwnerObjective.Sold },
                    { value: NonOwnerObjective.WantsToRent },
                    { value: NonOwnerObjective.IsCurrentTenant },
                    { value: NonOwnerObjective.Curious },
                    { value: NonOwnerObjective.IsSupporter },
                ],
            },
        },
    });
export const getProfileFields =
    (validators: Record<string, FieldValidator<unknown>>) =>
    (): {
        isAPrivatePerson: RadioBooleanField;
        job: SelectField;
        jobRole: SelectField;
        isPropertyOwner: RadioBooleanField;
        propertyMainUsage: SelectField;
        ownerObjective: SelectField;
        nonOwnerObjective: SelectField;
    } => ({
        isAPrivatePerson: {
            ...defaultRadioBooleanField,
            validators: validators.isAPrivatePerson,
            uiComponent: EUIComponent.RadioCard,
            options: {
                values: () => {
                    return [
                        {
                            value: true,
                            icon: EMediaComponent.Individual,
                            translationKey: 'individual',
                        },
                        {
                            value: false,
                            icon: EMediaComponent.Professional,
                            translationKey: 'professional',
                        },
                    ];
                },
            },
        },
        job: {
            ...defaultSelectField,
            validators: validators.job,
            options: {
                values: [
                    { value: Job.Realtor },
                    { value: Job.AssetAdvisor },
                    { value: Job.Insurer },
                    { value: Job.Developer },
                    { value: Job.Other },
                ],
            },
            displayedConditions: [
                (fieldValues: Record<string, any>) => fieldValues.isAPrivatePerson === false,
            ],
        },
        jobRole: {
            ...defaultSelectField,
            validators: validators.jobRole,
            options: {
                values: [
                    { value: JobRole.President },
                    { value: JobRole.Director },
                    { value: JobRole.AdvisorSales },
                    { value: JobRole.AdvisorRent },
                    { value: JobRole.Other },
                ],
            },
            displayedConditions: [
                (fieldValues: Record<string, any>) =>
                    !fieldValues.isAPrivatePerson && fieldValues.job === Job.Realtor,
            ],
        },
        ...getPropertyFields(validators)(),
    });

export const commonFloorNumberField = (validator: FieldValidator<unknown>): IncrementalField => ({
    ...defaultCustomIncrementalField,
    validators: validator,
    customValues: [{ value: 0, labelKey: 'groundfloor' }],
    displayedConditions: [
        (fieldValues: Record<string, any>) => fieldValues.estateType === EstateType.Apartment,
    ],
    maxValue: 100,
    relatedFieldToUpdate: {
        fieldName: 'numberOfFloorsInBuilding',
        conditions: [
            (fieldValues: Record<string, any>, fieldName: string, fieldToBeUpdated: string) => {
                const currentField =
                    fieldValues[fieldName] !== ''
                        ? formatStringWithPlusSign(fieldValues[fieldName])
                        : 0;
                const relatedField =
                    fieldValues[fieldToBeUpdated] !== ''
                        ? formatStringWithPlusSign(fieldValues[fieldToBeUpdated])
                        : 0;
                return (
                    fieldValues.estateType === EstateType.Apartment && relatedField <= currentField
                );
            },
        ],
    },
});

export const commonNumberOfFloorsInBuildingField = (
    validator: FieldValidator<unknown>,
): IncrementalField => ({
    ...defaultIncrementalField,
    validators: validator,
    maxValue: 100,
    minValue: 1,
    relatedFieldToUpdate: {
        fieldName: 'floorNumber',
        conditions: [
            (fieldValues: Record<string, any>, fieldName: string, fieldToBeUpdated: string) => {
                const currentField =
                    fieldValues[fieldName] !== ''
                        ? formatStringWithPlusSign(fieldValues[fieldName])
                        : 0;
                const relatedField =
                    fieldValues[fieldToBeUpdated] !== ''
                        ? formatStringWithPlusSign(fieldValues[fieldToBeUpdated])
                        : 0;
                return relatedField > currentField;
            },
        ],
    },
});

export const commonMonthlyCondominiumFeeField = (
    validator: FieldValidator<unknown>,
): NumberField => ({
    ...defaultNumberField,
    maxValue: 3_000,
    minValue: 1,
    displayedConditions: [(fieldValues) => fieldValues.estateType === EstateType.Apartment],
    isRequired: false,
    validators: validator,
});

export const generateStepsConfig = (
    validators: Record<string, FieldValidator<unknown>>,
    regularExpression: Record<string, RegExp>,
): ICommonStepsConfiguration => {
    return {
        address: {
            stepComponent: EStepComponent.Location,
            displayedStepNumber: 1,
            getFields: () => ({
                postalCode: {
                    ...defaultTextField,
                    type: 'text',
                    inputMode: 'numeric',
                    validators: validators.postalCode,
                },
                cityName: {
                    ...defaultSelectField,
                    validators: validators.cityName,
                    options: {
                        values: [],
                    },
                },
                streetName: {
                    ...defaultAutocompleteField,
                    validators: validators.streetName,
                    enabledConditions: [
                        (fieldValues: Record<string, any>) =>
                            fieldValues.postalCode && fieldValues.cityName,
                    ],
                },
                houseNumber: {
                    ...defaultTextField,
                    validators: validators.houseNumber,
                    enabledConditions: [
                        (fieldValues: Record<string, any>) =>
                            fieldValues.postalCode &&
                            fieldValues.cityName &&
                            fieldValues.streetName,
                    ],
                    isRequired: false,
                },
            }),
        },
        estateType: {
            displayedStepNumber: 2,
            getFields: () => ({
                estateType: {
                    ...defaultRadioField,
                    defaultValue: EstateType.Apartment,
                    validators: validators.estateType,
                    options: {
                        values: () => {
                            return [
                                {
                                    value: EstateType.Apartment,
                                    icon: EMediaComponent.Apartment,
                                },
                                {
                                    value: EstateType.House,
                                    icon: EMediaComponent.House,
                                },
                            ];
                        },
                    },
                    relatedFieldToFormat: {
                        fieldName: 'numberOfFloorsInBuilding',
                        stepName: 'characteristics',
                    },
                },
            }),
        },
        characteristics: {
            displayedStepNumber: 3,
            getFields: () => ({
                estateSubType: commonEstateSubTypeField(validators.estateSubType),
                livingSurface: commonLivingSurfaceField(validators.livingSurface),
                // House
                totalSiteSurface: commonTotalSiteSurfaceField(validators.totalSiteSurface),
                numberOfRooms: commonNumberOfRoomsField(validators.numberOfRooms),
                numberOfBathRooms: commonNumberOfBathRoomsField(validators.numberOfBathRooms),
                // Apartment
                floorNumber: commonFloorNumberField(validators.floorNumber),
                numberOfFloorsInBuilding: commonNumberOfFloorsInBuildingField(
                    validators.numberOfFloorsInBuilding,
                ),
                monthlyCondominiumFee: commonMonthlyCondominiumFeeField(
                    validators.monthlyCondominiumFee,
                ),
            }),
        },
        benefits: {
            displayedStepNumber: 4,
            getFields: (
                fieldValues: Record<string, any>,
            ): HouseAdditionalBenefits | ApartmentAdditionalBenefits => {
                if (fieldValues.estateType === EstateType.Apartment) {
                    return {
                        hasAnElevator: {
                            ...defaultCheckboxField,
                            validators: validators.hasAnElevator,
                        },
                        hasABalconyOrTerrace: {
                            ...defaultCheckboxField,
                            validators: validators.hasABalconyOrTerrace,
                            parent: ParentBenefit.BalconiesAndTerracesSurface,
                        },
                        balconiesAndTerracesSurface: {
                            ...defaultMaxRangeField,
                            validators: validators.balconiesAndTerracesSurface,
                            formatInputValue: formatFloatRange,
                            displayedConditions: [
                                (fieldValues: Record<string, any>) =>
                                    fieldValues.hasABalconyOrTerrace,
                            ],
                            maxValue: 1_000,
                            isRequired: false,
                            parent: ParentBenefit.BalconiesAndTerracesSurface,
                        },
                        hasAPrivateGarden: {
                            ...defaultCheckboxField,
                            validators: validators.hasAPrivateGarden,
                            parent: ParentBenefit.PrivateGarden,
                        },
                        privateGardensSurface: {
                            ...defaultMaxRangeField,
                            validators: validators.privateGardensSurface,
                            formatInputValue: formatFloatRange,
                            displayedConditions: [
                                (fieldValues: Record<string, any>) => fieldValues.hasAPrivateGarden,
                            ],
                            maxValue: 100_000,
                            isRequired: false,
                            parent: ParentBenefit.PrivateGarden,
                        },
                        hasACellar: {
                            ...defaultCheckboxField,
                            validators: validators.hasACellar,
                        },
                        hasAnOutdoorParking: {
                            ...defaultCheckboxField,
                            validators: validators.hasAnOutdoorParking,
                            parent: ParentBenefit.OutdoorParking,
                        },
                        numberOfOutdoorParkingLots: {
                            ...defaultIncrementalField,
                            validators: validators.numberOfOutdoorParkingLots,
                            displayedConditions: [
                                (fieldValues: Record<string, any>) =>
                                    fieldValues.hasAnOutdoorParking,
                            ],
                            maxValue: 100,
                            minValue: 1,
                            isRequired: false,
                            parent: ParentBenefit.OutdoorParking,
                        },
                        hasAnIndoorParking: {
                            ...defaultCheckboxField,
                            validators: validators.hasAnIndoorParking,
                            parent: ParentBenefit.IndoorParking,
                        },
                        numberOfIndoorParkingLots: {
                            ...defaultIncrementalField,
                            validators: validators.numberOfIndoorParkingLots,
                            displayedConditions: [
                                (fieldValues: Record<string, any>) =>
                                    fieldValues.hasAnIndoorParking,
                            ],
                            maxValue: 100,
                            minValue: 1,
                            isRequired: false,
                            parent: ParentBenefit.IndoorParking,
                        },
                        viewFromProperty: {
                            ...defaultRadioField,
                            uiComponent: EUIComponent.RadioCard,
                            styles: {
                                marginTop: true,
                            },
                            defaultValue: ViewFromProperty.Clear,
                            validators: validators.viewFromProperty,
                            options: {
                                values: [
                                    {
                                        value: ViewFromProperty.Blocked,
                                        icon: EMediaComponent.BlockedView,
                                    },
                                    {
                                        value: ViewFromProperty.Clear,
                                        icon: EMediaComponent.ClearView,
                                    },
                                    {
                                        value: ViewFromProperty.Outstanding,
                                        icon: EMediaComponent.OutstandingView,
                                    },
                                ],
                            },
                            isRequiredHidden: true,
                        },
                    };
                } else {
                    return {
                        hasABasement: {
                            ...defaultCheckboxField,
                            validators: validators.hasABasement,
                            parent: ParentBenefit.Basement,
                        },
                        basementsSurface: {
                            ...defaultMaxRangeField,
                            validators: validators.basementsSurface,
                            formatInputValue: formatFloatRange,
                            displayedConditions: [
                                (fieldValues: Record<string, any>) => fieldValues.hasABasement,
                            ],
                            maxValue: 10_000,
                            isRequired: false,
                            parent: ParentBenefit.Basement,
                        },
                        hasABalconyOrTerrace: {
                            ...defaultCheckboxField,
                            validators: validators.hasABalconyOrTerrace,
                            parent: ParentBenefit.BalconiesAndTerracesSurface,
                        },
                        balconiesAndTerracesSurface: {
                            ...defaultMaxRangeField,
                            validators: validators.balconiesAndTerracesSurface,
                            formatInputValue: formatFloatRange,
                            displayedConditions: [
                                (fieldValues: Record<string, any>) =>
                                    fieldValues.hasABalconyOrTerrace,
                            ],
                            maxValue: 1_000,
                            isRequired: false,
                            parent: ParentBenefit.BalconiesAndTerracesSurface,
                        },
                        hasAnOutdoorParking: {
                            ...defaultCheckboxField,
                            validators: validators.hasAnOutdoorParking,
                            parent: ParentBenefit.OutdoorParking,
                        },
                        numberOfOutdoorParkingLots: {
                            ...defaultIncrementalField,
                            validators: validators.numberOfOutdoorParkingLots,
                            displayedConditions: [
                                (fieldValues: Record<string, any>) =>
                                    fieldValues.hasAnOutdoorParking,
                            ],
                            maxValue: 100,
                            minValue: 1,
                            isRequired: false,
                            parent: ParentBenefit.OutdoorParking,
                        },
                        hasAnIndoorParking: {
                            ...defaultCheckboxField,
                            validators: validators.hasAnIndoorParking,
                            parent: ParentBenefit.IndoorParking,
                        },
                        numberOfIndoorParkingLots: {
                            ...defaultIncrementalField,
                            validators: validators.numberOfIndoorParkingLots,
                            displayedConditions: [
                                (fieldValues: Record<string, any>) =>
                                    fieldValues.hasAnIndoorParking,
                            ],
                            maxValue: 100,
                            minValue: 1,
                            isRequired: false,
                            parent: ParentBenefit.IndoorParking,
                        },
                        hasASwimmingPool: {
                            ...defaultCheckboxField,
                            validators: validators.hasASwimmingPool,
                        },
                        viewFromProperty: {
                            ...defaultRadioField,
                            uiComponent: EUIComponent.RadioCard,
                            styles: {
                                marginTop: true,
                            },
                            defaultValue: ViewFromProperty.Clear,
                            validators: validators.viewFromProperty,
                            options: {
                                values: [
                                    {
                                        value: ViewFromProperty.Blocked,
                                        icon: EMediaComponent.BlockedView,
                                    },
                                    {
                                        value: ViewFromProperty.Clear,
                                        icon: EMediaComponent.ClearView,
                                    },
                                    {
                                        value: ViewFromProperty.Outstanding,
                                        icon: EMediaComponent.OutstandingView,
                                    },
                                ],
                            },
                            isRequiredHidden: true,
                        },
                    };
                }
            },
        },
        conditions: {
            displayedStepNumber: 5,
            getFields: () => ({
                constructionYear: {
                    ...defaultMaxRangeField,
                    validators: validators.constructionYear,
                    displayedConditions: [
                        (fieldValues: Record<string, any>) =>
                            fieldValues.estateType === EstateType.House,
                    ],
                    isRequired: false,
                },
                constructionPeriod: {
                    ...defaultSelectField,
                    options: {
                        values: [
                            { value: EConstructionPeriod.Before1870 },
                            { value: EConstructionPeriod.Between1871And1913 },
                            { value: EConstructionPeriod.Between1914And1944 },
                            { value: EConstructionPeriod.Between1945And1969 },
                            { value: EConstructionPeriod.Between1970And1979 },
                            { value: EConstructionPeriod.Between1980And1989 },
                            { value: EConstructionPeriod.Between1990And1999 },
                            { value: EConstructionPeriod.Between2000And2009 },
                            { value: EConstructionPeriod.After2010 },
                            { value: EConstructionPeriod.IDontKnow },
                        ],
                    },
                    displayedConditions: [
                        (fieldValues: Record<string, any>) =>
                            fieldValues.estateType === EstateType.Apartment,
                    ],
                    isRequired: false,
                },
                epcEnergyEfficiencyRating: {
                    ...defaultSelectField,
                    validators: validators.epcEnergyEfficiencyRating,
                    options: {
                        values: [
                            { value: EpcEnergyEfficiencyRating.APlus },
                            { value: EpcEnergyEfficiencyRating.A },
                            { value: EpcEnergyEfficiencyRating.B },
                            { value: EpcEnergyEfficiencyRating.C },
                            { value: EpcEnergyEfficiencyRating.D },
                            { value: EpcEnergyEfficiencyRating.E },
                            { value: EpcEnergyEfficiencyRating.F },
                            { value: EpcEnergyEfficiencyRating.G },
                            { value: EpcEnergyEfficiencyRating.H },
                            { value: EpcEnergyEfficiencyRating.DiagnosisNotRequired },
                            { value: EpcEnergyEfficiencyRating.IDontKnow },
                        ],
                    },
                    isRequired: false,
                },
                averageCondition: {
                    ...defaultRadioField,
                    uiComponent: EUIComponent.PropertyRating,
                    defaultValue: PropertyCondition.Nice,
                    validators: validators.averageCondition,
                    options: {
                        values: [
                            { value: PropertyCondition.Bad, icon: '😞' },
                            { value: PropertyCondition.Average, icon: '😐' },
                            { value: PropertyCondition.Nice, icon: '🙂' },
                            { value: PropertyCondition.Great, icon: '😎' },
                            { value: PropertyCondition.Perfect, icon: '😍' },
                        ],
                    },
                    isRequiredHidden: true,
                },
            }),
        },
        profile: {
            displayedStepNumber: 6,
            stepComponent: EStepComponent.Profile,
            getFields: getProfileFields(validators),
        },
        identity: {
            displayedStepNumber: 6,
            stepComponent: EStepComponent.Identity,
            getFields: () => ({
                salutation: {
                    ...defaultRadioField,
                    uiComponent: EUIComponent.Radio,
                    validators: validators.salutation,
                    options: {
                        values: [
                            { value: Salutation.Ms },
                            { value: Salutation.Mr },
                            { value: Salutation.Other },
                        ],
                    },
                },
                firstName: {
                    ...defaultTextField,
                    validators: validators.firstName,
                    autocomplete: 'given-name',
                },
                lastName: {
                    ...defaultTextField,
                    validators: validators.lastName,
                    autocomplete: 'family-name',
                },
                phoneNumberRegionCode: {
                    ...defaultSelectField,
                    defaultValue: RegionCode.Germany,
                    validators: validators.phoneNumberRegionCode,
                    options: {
                        values: getPhoneNumberRegionCodeValues(GermanRegionCodes),
                    },
                    relatedFieldToValidate: {
                        fieldName: 'phoneNumber',
                    },
                    autocomplete: 'tel-country-code',
                },
                phoneNumber: {
                    ...defaultTextField,
                    inputMode: 'tel',
                    type: 'tel',
                    validators: validators.phoneNumber,
                    autocomplete: 'tel-national',
                },
                email: {
                    ...defaultTextField,
                    validators: validators.email,
                    autocomplete: 'email',
                    inputMode: 'email',
                    type: 'email',
                },
            }),
            httpCalls: {
                validatePhone,
                submitForm,
            },
            customSubmit: useSmsService,
        },
        verification: {
            displayedStepNumber: 6,
            stepComponent: EStepComponent.PhoneNumberVerification,
            getFields: () => ({
                phoneNumberVerificationCode: {
                    ...defaultTextField,
                    type: 'text',
                    inputMode: 'numeric',
                    uiComponent: EUIComponent.PhoneNumberVerificationCode,
                    validators: dv.string().format(regularExpression.PhoneNumberVerificationCode),
                    isRequiredHidden: true,
                },
            }),
            httpCalls: {
                validatePhone,
                submitForm,
            },
            customSubmit: submitFormattedForm,
        },
        loading: {
            displayedStepNumber: 7,
            getFields: () => ({}),
        },
    };
};
